import { Component, OnInit, TemplateRef } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { finalize } from 'rxjs/operators';
import {
  LogicielCaisseTypeModePaimentService,
  TYPE_MODE_LOGO_CONSTRAINTS,
  TypeModePaiment,
  TypeModePaimentPayload,
} from 'app/services/logiciel-caisse/logiciel-caisse-type-mode-paiment.service';

@Component({
  selector: 'app-logiciel-caisse-type-mode-paiment',
  templateUrl: './logiciel-caisse-type-mode-paiment.component.html',
  styleUrls: ['./logiciel-caisse-type-mode-paiment.component.scss'],
})
export class LogicielCaisseTypeModePaimentComponent implements OnInit {
  readonly logoConstraints = TYPE_MODE_LOGO_CONSTRAINTS;

  rows: TypeModePaiment[] = [];
  filteredRows: TypeModePaiment[] = [];
  searchTerm = '';
  loading = false;
  saving = false;
  editId: number | null = null;
  form: TypeModePaimentPayload = this.emptyForm();

  /** Preview data-URL ou URL serveur. */
  logoPreview: string | null = null;
  /** Logo déjà stocké (édition). */
  existingLogo: string | null = null;
  selectedLogoFile: File | null = null;
  removeLogo = false;
  logoError = '';

  constructor(
    private readonly typeModeService: LogicielCaisseTypeModePaimentService,
    private readonly modalService: NgbModal,
    private readonly toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.loadRows();
  }

  private emptyForm(): TypeModePaimentPayload {
    return {
      libelle: '',
      is_sodexo: false,
      is_manuelle: false,
      affectation: 'caisse',
      type: null,
      active: true,
      mode: '',
    };
  }

  loadRows(): void {
    this.loading = true;
    this.typeModeService
      .getAll()
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (rows) => {
          this.rows = rows;
          this.applySearch();
        },
        error: (err: Error) => {
          this.rows = [];
          this.filteredRows = [];
          this.toastr.error(err.message || 'Chargement impossible', 'Modes de paiement');
        },
      });
  }

  applySearch(): void {
    const q = this.searchTerm.trim().toLowerCase();
    if (!q) {
      this.filteredRows = [...this.rows];
      return;
    }
    this.filteredRows = this.rows.filter((r) => {
      const hay = [r.libelle, r.mode, r.affectation, r.type]
        .map((x) => String(x || '').toLowerCase())
        .join(' ');
      return hay.includes(q);
    });
  }

  logoSrc(row: TypeModePaiment): string | null {
    return this.typeModeService.logoUrl(row.logo);
  }

  initialOf(libelle: string | null | undefined): string {
    return this.typeModeService.initialLetter(libelle);
  }

  formInitial(): string {
    return this.initialOf(this.form.libelle);
  }

  openCreate(tpl: TemplateRef<unknown>): void {
    this.editId = null;
    this.form = this.emptyForm();
    this.resetLogoState();
    this.modalService.open(tpl, { centered: true, backdrop: 'static', size: 'lg' });
  }

  openEdit(row: TypeModePaiment, tpl: TemplateRef<unknown>): void {
    this.editId = row.id;
    this.form = {
      libelle: row.libelle || '',
      is_sodexo: !!(row.is_sodexo === true || row.is_sodexo === 1),
      is_manuelle: !!(row.is_manuelle === true || row.is_manuelle === 1),
      affectation: row.affectation ?? 'caisse',
      type: row.type ?? null,
      active: !(row.active === false || row.active === 0),
      mode: row.mode ?? '',
    };
    this.resetLogoState();
    this.existingLogo = row.logo || null;
    this.logoPreview = this.typeModeService.logoUrl(row.logo);
    this.modalService.open(tpl, { centered: true, backdrop: 'static', size: 'lg' });
  }

  private resetLogoState(): void {
    this.logoPreview = null;
    this.existingLogo = null;
    this.selectedLogoFile = null;
    this.removeLogo = false;
    this.logoError = '';
  }

  onLogoSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    this.logoError = '';
    this.selectedLogoFile = null;
    if (!file) return;

    if (file.size > this.logoConstraints.maxBytes) {
      this.logoError = `Fichier trop lourd (max ${Math.round(this.logoConstraints.maxBytes / 1024)} Ko).`;
      input.value = '';
      return;
    }
    if (!/^image\/(png|jpe?g|webp|gif)$/i.test(file.type)) {
      this.logoError = 'Format invalide (png, jpg, webp, gif).';
      input.value = '';
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      const w = img.naturalWidth;
      const h = img.naturalHeight;
      if (w > this.logoConstraints.maxPx || h > this.logoConstraints.maxPx) {
        this.logoError = `Dimensions trop grandes (${w}×${h}). Maximum ${this.logoConstraints.maxPx}×${this.logoConstraints.maxPx} px.`;
        input.value = '';
        return;
      }
      this.selectedLogoFile = file;
      this.removeLogo = false;
      const reader = new FileReader();
      reader.onload = () => {
        this.logoPreview = reader.result as string;
      };
      reader.readAsDataURL(file);
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      this.logoError = 'Image illisible.';
      input.value = '';
    };
    img.src = objectUrl;
  }

  clearLogo(): void {
    this.selectedLogoFile = null;
    this.logoPreview = null;
    this.logoError = '';
    if (this.existingLogo) {
      this.removeLogo = true;
    }
  }

  save(modal: { close: () => void }): void {
    const libelle = String(this.form.libelle || '').trim();
    if (!libelle) {
      this.toastr.error('Le libellé est obligatoire', 'Modes de paiement');
      return;
    }
    if (this.logoError) {
      this.toastr.error(this.logoError, 'Modes de paiement');
      return;
    }
    const payload: TypeModePaimentPayload = {
      libelle,
      is_sodexo: !!this.form.is_sodexo,
      is_manuelle: !!this.form.is_manuelle,
      affectation: String(this.form.affectation || '').trim() || null,
      type: String(this.form.type || '').trim() || null,
      active: this.form.active !== false,
      mode: String(this.form.mode || '').trim() || null,
      remove_logo: this.removeLogo && !this.selectedLogoFile,
    };
    this.saving = true;
    const req$ =
      this.editId != null
        ? this.typeModeService.update(this.editId, payload, this.selectedLogoFile)
        : this.typeModeService.create(payload, this.selectedLogoFile);
    req$.pipe(finalize(() => (this.saving = false))).subscribe({
      next: () => {
        this.toastr.success(this.editId != null ? 'Mode mis à jour' : 'Mode créé', 'Modes de paiement');
        modal.close();
        this.loadRows();
      },
      error: (err: Error) => {
        this.toastr.error(err.message || 'Erreur', 'Modes de paiement');
      },
    });
  }

  deactivate(row: TypeModePaiment): void {
    if (!row?.id) return;
    if (!confirm(`Désactiver le mode « ${row.libelle} » ?`)) return;
    this.typeModeService.deactivate(row.id).subscribe({
      next: () => {
        this.toastr.success('Mode désactivé', 'Modes de paiement');
        this.loadRows();
      },
      error: (err: Error) => {
        this.toastr.error(err.message || 'Erreur', 'Modes de paiement');
      },
    });
  }

  isActive(row: TypeModePaiment): boolean {
    return !(row.active === false || row.active === 0);
  }

  boolLabel(v: unknown): string {
    return v === true || v === 1 || v === '1' ? 'Oui' : 'Non';
  }
}
